import React from 'react';
import normalizeLocationId from 'components/LocationTree/utils/normalizeLocationId';
import clsx from 'clsx';
import { KeyboardArrowRight as KeyboardArrowRightIcon } from '@mui/icons-material';

import {
  Checkbox,
  IconButton,
  Typography,
  TYPOGRAPHY_TOKENS,
  COLORS,
} from 'pvs-design-system';
import CircularProgress from '@mui/material/CircularProgress/CircularProgress';
import hasSelectedDescendants from 'components/LocationTree/utils/checkboxState';
import type { BranchProps, Location } from '../../types';
import style from './LocationTreeBranch.module.css';
import { useLocationTreeContext } from '../../context/LocationTreeContext';

const LocationTreeBranch: React.FC<BranchProps> = ({
  branch,
  depth,
  onToggleOpen,
}) => {
  const { selectedLocations, onSelectedChange, focusedNodeId } =
    useLocationTreeContext();
  const branchId = `location-tree-branch-${branch.id}`;
  const checkboxId = `location-tree-checkbox-${branch.id}`;

  const normalizedBranchId = normalizeLocationId(branch.id);
  const isFocused = focusedNodeId === normalizedBranchId;
  const isChecked = selectedLocations.some(
    (loc: Location) => normalizeLocationId(loc.id) === normalizedBranchId
  );
  const hasChildrenSelected = hasSelectedDescendants(branch, selectedLocations);

  const isIndeterminate = !isChecked && hasChildrenSelected;

  const displayExpansionIcon = () => {
    if (branch.isLoading) {
      return <CircularProgress size={24} />;
    }

    return branch.isOpen ? (
      <KeyboardArrowRightIcon
        style={{ height: 24, width: 24, transform: 'rotate(90deg)' }}
      />
    ) : (
      <KeyboardArrowRightIcon style={{ height: 24, width: 24 }} />
    );
  };

  return (
    <div
      id={branchId}
      className={clsx(style.locationTreeBranch, isFocused && style.focused)}
      style={
        {
          '--depth': depth,
          '--focus-color': COLORS.purple[100],
        } as React.CSSProperties
      }
    >
      <IconButton
        className={style.iconWrapper}
        onClick={() => onToggleOpen(branch)}
        aria-label={
          branch.isOpen ? `Collapse ${branch.name}` : `Expand ${branch.name}`
        }
        tabIndex={-1}
      >
        {displayExpansionIcon()}
      </IconButton>
      <div className={style.contentWrapper}>
        <Checkbox
          tabIndex={-1}
          color="primary"
          checked={isChecked}
          indeterminate={isIndeterminate}
          onChange={() => onSelectedChange(branch)}
          id={checkboxId}
          aria-label={`Select ${branch.name}`}
        />
        <Typography variant="p-md" sx={{ ...TYPOGRAPHY_TOKENS['p-md'] }}>
          {branch.name}
        </Typography>
      </div>
    </div>
  );
};

export default LocationTreeBranch;
